/**
 * Performance optimization utilities for CHM Construction website
 * Implements performance best practices and monitoring
 */
import React from 'react';

// Image optimization utility
export const getOptimizedImageProps = (src: string, alt: string, width?: number, height?: number) => {
  const isLCP = src.includes('fea6bb19-796f-4b2d-8258'); // Hero image
  
  return {
    src,
    alt,
    width,
    height,
    loading: (isLCP ? 'eager' : 'lazy') as 'eager' | 'lazy',
    decoding: 'async' as 'async',
    ...(width && height && {
      style: { aspectRatio: `${width}/${height}` }
    })
  };
};

// Lazy loading utility for components
export const createLazyComponent = <T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) => {
  return React.lazy(importFn);
};

// Debounce utility for performance
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(null, args), wait);
  };
};

// Throttle utility for scroll events
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func.apply(null, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Performance monitoring
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: { [key: string]: number } = {};

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  startMeasure(name: string): void {
    this.metrics[name] = performance.now();
  }

  endMeasure(name: string): number {
    const startTime = this.metrics[name];
    if (!startTime) return 0;
    
    const duration = performance.now() - startTime;
    delete this.metrics[name];
    
    // Log slow operations (over 100ms)
    if (duration > 100) {
      console.warn(`Slow operation detected: ${name} took ${duration.toFixed(2)}ms`);
    }
    
    return duration;
  }

  measureCLS(): void {
    if ('LayoutShift' in window) {
      new PerformanceObserver((list) => {
        let clsValue = 0;
        
        for (const entry of list.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        }
        
        if (clsValue > 0.1) {
          console.warn(`High CLS detected: ${clsValue}`);
        }
      }).observe({ type: 'layout-shift', buffered: true });
    }
  }

  measureLCP(): void {
    if ('LargestContentfulPaint' in window) {
      new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        if (lastEntry.startTime > 2500) {
          console.warn(`Slow LCP detected: ${lastEntry.startTime}ms`);
        }
      }).observe({ type: 'largest-contentful-paint', buffered: true });
    }
  }

  measureFID(): void {
    if ('FirstInputDelay' in window) {
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const fidEntry = entry as any; // Type assertion for FID entry
          const fid = fidEntry.processingStart - fidEntry.startTime;
          
          if (fid > 100) {
            console.warn(`High FID detected: ${fid}ms`);
          }
        }
      }).observe({ type: 'first-input', buffered: true });
    }
  }

  init(): void {
    this.measureCLS();
    this.measureLCP();
    this.measureFID();
  }
}

// Resource preloading utility
export const preloadResource = (href: string, as: string, crossorigin?: boolean) => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = href;
  link.as = as;
  if (crossorigin) link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
};

// Critical resource preloading
export const preloadCriticalResources = () => {
  // Preload hero image
  preloadResource('/lovable-uploads/fea6bb19-796f-4b2d-8258-a90434511cdc.png', 'image');
  
  // Preload external font icons if needed
  preloadResource('https://cdn-icons-png.flaticon.com/512/2991/2991148.png', 'image', true);
};

export default PerformanceMonitor;